import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APPS_NAV_LINK } from 'src/content/MiniExperts/constants';
import {
  getPendingUploadUUID,
  setPendingUploadUUID
} from 'src/content/MiniExperts/SmartMeet/utils/pendingUpload';
import { resolveProcessingRoute } from 'src/content/MiniExperts/SmartMeet/utils/utils';
import { AzureWebPubSubUserContext } from 'src/contexts/AzureWebPubSubUserContext';
import useAnalytics from 'src/hooks/useAnalytics';
import { useAlert } from 'src/hooks/useAlert';
import {
  incrementRefreshKey,
  RecordingProgress,
  setProgress
} from 'src/redux/slices/smartMeet';
import { useDispatch, useSelector } from 'src/redux/store';
import { SMART_MEET_SUMMARY_GENERATED } from 'src/utils/analyticsUtils';
import { ALERT, SMART_MEET_EVENT_TYPE } from 'src/types/enum';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { getEnumKeyByValue } from 'src/utils/utils';

export const useSmartMeetWebSocket = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const collectAnalytics = useAnalytics();
  const { processedData, resetContext } = useContext(AzureWebPubSubUserContext);

  const isCurrentMeeting = (meetingId: number, eventUUID?: string) => {
    if (window.location.pathname.endsWith(`/${meetingId}`)) return true;

    const pendingUUID = getPendingUploadUUID();
    return !!pendingUUID && !!eventUUID && pendingUUID === eventUUID;
  };

  useEffect(() => {
    const eventType = processedData?.event_type;
    if (eventType !== SMART_MEET_EVENT_TYPE.STATUS) return;

    const meetingId =
      processedData?.data?.meeting_id ||
      processedData?.data?.response?.meeting_id;

    const progress = getEnumKeyByValue(
      RecordingProgress,
      processedData?.data?.status_code
    );

    if (progress && meetingId) {
      const eventUUID = processedData?.uuid;
      const isFailed = processedData?.data?.state === false;
      const isMeetingCreated =
        RecordingProgress[progress] === RecordingProgress.MeetingCreated &&
        !isFailed;

      if (isCurrentMeeting(meetingId, eventUUID)) {
        if (isMeetingCreated) {
          collectAnalytics(SMART_MEET_SUMMARY_GENERATED, { meeting_id: meetingId });
          dispatch(setProgress(null));

          if (!window.location.pathname.endsWith(`/${meetingId}`)) {
            navigate(`${APPS_NAV_LINK.SMART_MEET}/${meetingId}`, {
              replace: true
            });
          }
        } else {
          dispatch(
            setProgress({
              status_code: RecordingProgress[progress],
              state: processedData?.data?.state ?? true,
              status_details: processedData?.data?.status_details
            })
          );

          if (isFailed) {
            showAlert(ALERT.ERROR, t(T.meetingProcessingFailed));
          }
        }

        if (eventUUID && getPendingUploadUUID() === eventUUID) {
          setPendingUploadUUID(null);
          resolveProcessingRoute(meetingId);
        }
      } else {
        dispatch(incrementRefreshKey());
      }
    }

    resetContext();
  }, [processedData]);
};
